import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TOKEN_KEY } from "../lib/mw";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Bot, Loader2, Plus, Send, Pencil, Trash2 } from "lucide-react";
import { SecretInput, n4 } from "./AiSitesModal";

export default function AiChatModal({ open, onOpenChange }) {
  const { isSuperAdmin, selectedTenant } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const endRef = useRef(null);

  const loadSessions = () => api.get("/ai-chat/sessions").then((r) => setSessions(r.data));
  useEffect(() => {
    if (!open) return;
    loadSessions();
    if (!isSuperAdmin) api.get("/ai-credits/me").then((r) => setInfo({ bakiye: r.data.bakiye, fiyat: r.data.fiyat_soru }));
  }, [open, isSuperAdmin]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs]);

  const openSession = async (id) => { setSid(id); setMsgs((await api.get(`/ai-chat/sessions/${id}`)).data.messages); };
  const rename = async (x) => { const t = window.prompt("SOHBET ADI:", x.title); if (!t || !t.trim()) return; await api.put(`/ai-chat/sessions/${x.id}`, { data: { title: t } }); loadSessions(); };
  const remove = async (x) => { if (!window.confirm(`"${x.title}" SOHBETİ SİLİNSİN Mİ?`)) return; await api.delete(`/ai-chat/sessions/${x.id}`); if (x.id === sid) newChat(); loadSessions(); };
  const newChat = () => { setSid(null); setMsgs([]); setErr(""); };

  const send = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setText(""); setErr(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` };
    if (selectedTenant) headers["X-Tenant-Id"] = selectedTenant;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-chat/send`, { method: "POST", headers, body: JSON.stringify({ text: q, session_id: sid, pin }) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "HATA"); }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const d = JSON.parse(p.slice(6));
          if (d.session_id) setSid(d.session_id);
          if (d.t) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d.t }; return c; });
          if (d.error) setErr(d.error);
          if (d.done) setInfo((i) => ({ ...i, son: d.kredi, ...(d.bakiye != null ? { bakiye: d.bakiye } : {}) }));
        }
      }
      loadSessions();
    } catch (e) { setErr(e.message); setMsgs((m) => m.slice(0, -2)); setText(q); }
    setBusy(false);
  };

  const noCredit = !isSuperAdmin && info && info.bakiye < (info.fiyat || 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ai-chat-modal" className="flex h-[80vh] max-w-4xl flex-col border-slate-700 bg-slate-900 text-slate-100">
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Bot className="h-5 w-5 text-fuchsia-400" /> YAPAY ZEKAYA SOR</DialogTitle></DialogHeader>
        <div data-testid="ai-chat-info" className="flex flex-wrap items-center gap-x-4 rounded border border-fuchsia-900 bg-fuchsia-950/40 px-2 py-1.5 text-xs font-bold text-fuchsia-200">
          {isSuperAdmin ? <span>YAPIMCI ADMİN — PIN İLE ÜCRETSİZ</span> : <><span data-testid="ai-chat-balance">KREDİNİZ: {n4(info?.bakiye)}</span><span>SORU BAŞI: {n4(info?.fiyat)} KREDİ</span></>}
          {info?.son != null && <span data-testid="ai-chat-last">BU SORU: {n4(info.son)} KREDİ</span>}
        </div>
        {isSuperAdmin && <SecretInput testid="ai-chat-pin" value={pin} onChange={setPin} placeholder="PIN" />}
        {noCredit && <div data-testid="ai-chat-no-credit" className="rounded border border-rose-600 bg-rose-950/60 px-2 py-1.5 text-xs font-extrabold text-rose-200">KREDİNİZ YETERSİZ — MÜŞTERİLER › İŞLEMLER › YAPAY ZEKA FİRMA EKLE › KREDİ AL</div>}
        <div className="flex min-h-0 flex-1 gap-2">
          <div className="w-44 shrink-0 space-y-1 overflow-auto border-r border-slate-800 pr-2 text-xs">
            <Button data-testid="ai-chat-new" onClick={newChat} className="h-8 w-full bg-slate-700 text-xs hover:bg-slate-600"><Plus className="mr-1 h-3.5 w-3.5" /> YENİ SOHBET</Button>
            {sessions.map((s) => (
              <div key={s.id} className={`group flex items-center rounded ${s.id === sid ? "bg-fuchsia-900 text-white" : "text-slate-300 hover:bg-slate-800"}`}>
                <button data-testid={`ai-chat-session-${s.id}`} onClick={() => openSession(s.id)} className="min-w-0 flex-1 truncate px-2 py-1 text-left">{s.title}</button>
                <button data-testid={`ai-chat-rename-${s.id}`} onClick={() => rename(s)} title="ADINI DEĞİŞTİR" className="p-1 text-slate-400 hover:text-white"><Pencil className="h-3 w-3" /></button>
                <button data-testid={`ai-chat-delete-${s.id}`} onClick={() => remove(s)} title="SİL" className="p-1 text-slate-400 hover:text-rose-400"><Trash2 className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
          <div data-testid="ai-chat-messages" className="min-h-0 flex-1 space-y-2 overflow-auto pr-1 text-sm">
            {msgs.length === 0 && <div className="pt-8 text-center text-xs text-slate-500">HER KONUDA SORU SORABİLİRSİNİZ. SİSTEMİ NASIL KULLANACAĞINIZI DA SORABİLİRSİNİZ.</div>}
            {msgs.map((m, i) => <div key={i} data-testid={`ai-chat-msg-${i}`} className={`whitespace-pre-wrap rounded-lg px-3 py-2 ${m.role === "user" ? "ml-10 bg-blue-900/60" : "mr-10 bg-slate-800"}`}>{m.content || (busy && i === msgs.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}</div>)}
            <div ref={endRef} />
          </div>
        </div>
        {err && <div data-testid="ai-chat-error" className="text-xs font-bold text-rose-400">{err}</div>}
        <div className="flex gap-2">
          <textarea data-testid="ai-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={2} placeholder="SORUNUZU YAZIN (ENTER = GÖNDER)" className="flex-1 rounded-md border border-slate-700 bg-slate-800 p-2 text-sm text-slate-100" />
          <Button data-testid="ai-chat-send" disabled={busy || !text.trim() || noCredit || (isSuperAdmin && !pin.trim())} onClick={send} className="h-auto bg-fuchsia-600 hover:bg-fuchsia-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
